import { Grid } from "@mui/material";
import { AnalyticsSpacing } from "consts";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import {
  table5RowsWithComp,
  useSelectedAttributes,
  useSelectedDimension,
  useSelectedMetric,
} from "features/analytics";
import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import Loading from "Components/Loading/Loading";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";

interface IInsightsBlockProps {
  dimension?: ApiDimension;
  metric?: ApiMetric;
  attributes?: ApiChartAttribute[];
}

function InsightsBlock({
  dimension: propDimension,
  metric: propMetric,
  attributes: propAttributes,
}: IInsightsBlockProps) {
  const { selectedDimension } = useSelectedDimension();
  const { selectedMetric } = useSelectedMetric();
  const { selectedAttributes } = useSelectedAttributes();

  const dimension = propDimension ?? selectedDimension;
  const metric = propMetric ?? selectedMetric;
  const attributes = propAttributes ?? selectedAttributes;

  const dimensionToName = useDimensionName();
  if (!metric) {
    return <Loading fullHeight />;
  }
  return (
    <Grid container spacing={AnalyticsSpacing}>
      <Grid item xs={5}>
        <AnalyticsCard
          height={table5RowsWithComp}
          chart={{
            name: `${dimensionToName(dimension)} Breakdown`,
            dimension: [dimension],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: attributes || [],
            comparison: true,
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields:
                  metric === ApiMetric.METRIC_SESSIONS_COUNT
                    ? [ApiMetric.METRIC_SESSIONS_COUNT]
                    : [ApiMetric.METRIC_SESSIONS_COUNT, metric],
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              rowsPerPage: 5,
              showTotals: true,
              disabledMetricClick: true,
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <AnalyticsCard
          height={table5RowsWithComp}
          chart={{
            name: `Trending ${dimensionToName(dimension, true)}`,
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY, dimension],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: attributes || [],
            limit: 5,
            includePublisherReadyOnly: true,
            orderBy: `${ApiMetric.METRIC_SESSIONS_COUNT} DESC`,
            yAxisKeys: [
              {
                key: "l",
                fields: [metric],
                chart: {
                  [metric]: ApiYAxisChartType.AREA,
                },
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              disabledMetricClick: true,
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <AnalyticsCard
          height={table5RowsWithComp}
          chart={{
            name:
              dimension === ApiDimension.DIMENSION_DEVICE
                ? `Device Composition`
                : `Top 5 ${dimensionToName(dimension, true)}`,
            dimension: [dimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: attributes || [],
            limit: 5,
            includePublisherReadyOnly: true,
            orderBy: `${ApiMetric.METRIC_SESSIONS_COUNT} DESC`,
            yAxisKeys: [
              {
                key: "l",
                fields: [metric],
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              showTotals: true,
              disabledMetricClick: true,
              noFilters: true,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default InsightsBlock;
